import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './ContactUs.css';
import { company1, company2, company3, company4 } from '../assets';

const ContactUs = () => {
  const navigate = useNavigate();
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });
  const [contactRef, contactInView] = useInView({ threshold: 0.1 });
  const [infoRef, infoInView] = useInView({ threshold: 0.1 });

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    service: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Using Formspree worker for testing
      const response = await fetch('https://contact-form-handler.suitematrixmanagement.workers.dev/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitStatus('success');
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          subject: '',
          message: '',
          service: ''
        });
        // Redirect to thank you page after brief delay
        setTimeout(() => {
          navigate('/thank-you');
        }, 1500);
      } else {
        setSubmitStatus('error');
        console.error('Form submission failed:', data);
      }
    } catch (error) {
      setSubmitStatus('error');
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const slideInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const contactInfo = [
    {
      icon: "bi-geo-alt-fill",
      title: "Our Office",
      details: ["5886 Silhouette Ct,", "Dublin, California,", "USA 94568"],
      color: "#FF7A00"
    },
    {
      icon: "bi-telephone-fill",
      title: "Phone Numbers",
      details: ["+1 (650) 9632560",  "24/7 Support Available"],
      color: "#0066CC"
    },
    {
      icon: "bi-envelope-fill",
      title: "Email Addresses",
      details: ["business@spirenest.co"],
      color: "#FF7A00"
    },
    
  ];

  const services = [
    "NetSuite Implementation",
    "NetSuite Support",
    "NetSuite Training",
    "Custom Development",
    "Data Migration",
    "Integration Services",
    "Consultation",
    "Other"
  ];

  return (
    <div className="contact-us-page">
      <Helmet>
        <title>Contact SpireNest | NetSuite Services USA</title>
        <link rel="canonical" href="https://spirenest.co/contact" />
        <meta name="description" content="Get in touch with SpireNest for expert NetSuite services across the U.S. Our team is ready to assist with your ERP needs." />
        <meta name="keywords" content="Contact SpireNest, NetSuite experts, ERP services, CRM services, USA, NetSuite support" />
        <meta property="og:title" content="Contact SpireNest | NetSuite ERP Experts in the USA" />
        <meta property="og:description" content="Connect with SpireNest for expert NetSuite implementation, customization, and support." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://spirenest.co/contact-us" />
        <meta property="og:image" content="https://spirenest.co/assets/images/og-contact.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact SpireNest | NetSuite ERP Experts in the USA" />
        <meta name="twitter:description" content="Let's talk about your NetSuite needs. Book a free consultation today." />
        <meta name="twitter:image" content="https://spirenest.co/assets/images/og-contact.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebPage",
                "@id": "https://spirenest.co/contact-us",
                "name": "Contact SpireNest | NetSuite ERP Experts in the USA",
                "description": "Connect with SpireNest for expert NetSuite implementation, customization, and support.",
                "url": "https://spirenest.co/contact-us",
                "image": "https://spirenest.co/assets/images/og-contact.jpg",
                "publisher": {
                  "@type": "Organization",
                  "name": "SpireNest",
                  "url": "https://spirenest.co/",
                  "logo": {
                    "@type": "ImageObject",
                    "url": "https://spirenest.co/assets/logo-Cko-LaIl.png"
                  }
                }
              },
              {
                "@type": "BreadcrumbList",
                "@id": "https://spirenest.co/contact-us#breadcrumb",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://spirenest.co/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Contact Us",
                    "item": "https://spirenest.co/contact-us"
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="hero-section position-relative overflow-hidden">
        <div className="hero-bg-overlay"></div>
        <div className="floating-shapes">
          <motion.div className="shape-1" animate={{ rotate: 360, y: [-20, 20] }} transition={{ duration: 20, repeat: Infinity }} />
          <motion.div className="shape-2" animate={{ rotate: -360, x: [-30, 30] }} transition={{ duration: 25, repeat: Infinity }} />
          <motion.div className="shape-3" animate={{ y: [-15, 15], rotate: 180 }} transition={{ duration: 15, repeat: Infinity }} />
        </div>
        
        <Container className="position-relative">
          <Row className="align-items-center min-vh-100">
            <Col lg={12}>
              <motion.div
                ref={heroRef}
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                variants={staggerContainer}
                className="hero-content text-center"
              >
                <motion.div variants={fadeInUp}>
                  <Badge className="hero-badge mb-4">
                    <i className="bi-headset me-2"></i>
                    GET IN TOUCH
                  </Badge>
                </motion.div>
                
                <motion.h1 variants={slideInLeft} className="hero-title">
                  Contact <span className="text-white">SpireNest</span>
                </motion.h1>
                
                <motion.p variants={slideInLeft} className="hero-description">
                  Ready to transform your business with NetSuite? Get in touch with our expert team today. 
                  We're here to help you navigate your digital transformation journey and achieve your business goals.
                </motion.p>
                
                {/* <motion.div variants={fadeInUp} className="hero-buttons">
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button className="btn-primary-custom btn-lg me-3 px-4 py-3">
                      <i className="bi-chat-dots me-2"></i>
                      Start Conversation
                    </Button>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button variant="outline-light" className="btn-lg px-4 py-3">
                      <i className="bi-calendar-check me-2"></i>
                      Schedule Meeting
                    </Button>
                  </motion.div>
                </motion.div> */}
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Contact Form & Info Section */}
      <section className="contact-section py-5">
        <Container>
          <motion.div
            ref={contactRef}
            initial="hidden"
            animate={contactInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <Row className="g-5">
              {/* Contact Form */}
              <Col lg={8}>
                <motion.div variants={slideInLeft}>
                  <Card className="contact-form-card border-0 shadow-lg">
                    <Card.Body className="p-5">
                      <h3 className="contact-form-title mb-4">Send Us a Message</h3>
                      <Form onSubmit={handleSubmit}>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>First Name *</Form.Label>
                              <Form.Control
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                required
                                className="form-control-custom"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Last Name *</Form.Label>
                              <Form.Control
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                required
                                className="form-control-custom"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Email Address *</Form.Label>
                              <Form.Control
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                className="form-control-custom"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Phone Number</Form.Label>
                              <Form.Control
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                className="form-control-custom"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Company Name</Form.Label>
                              <Form.Control
                                type="text"
                                name="company"
                                value={formData.company}
                                onChange={handleInputChange}
                                className="form-control-custom"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Service Interest</Form.Label>
                              <Form.Select
                                name="service"
                                value={formData.service}
                                onChange={handleInputChange}
                                className="form-control-custom"
                              >
                                <option value="">Select a service...</option>
                                {services.map((service, index) => (
                                  <option key={index} value={service}>{service}</option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        <Form.Group className="mb-3">
                          <Form.Label>Subject *</Form.Label>
                          <Form.Control
                            type="text"
                            name="subject"
                            value={formData.subject}
                            onChange={handleInputChange}
                            required
                            className="form-control-custom"
                          />
                        </Form.Group>
                        
                        <Form.Group className="mb-4">
                          <Form.Label>Message *</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={5}
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            required
                            className="form-control-custom"
                          />
                        </Form.Group>

                        {submitStatus === 'success' && (
                          <div className="alert alert-success mb-3" role="alert">
                            <i className="bi-check-circle-fill me-2"></i>
                            Message sent successfully! We'll get back to you soon.
                          </div>
                        )}

                        {submitStatus === 'error' && (
                          <div className="alert alert-danger mb-3" role="alert">
                            <i className="bi-exclamation-triangle-fill me-2"></i>
                            Failed to send message. Please try again or email us directly at business@spirenest.co
                          </div>
                        )}

                        <motion.div
                          whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                          whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                        >
                          <Button
                            type="submit"
                            className="btn-submit-custom btn-lg px-5 py-3"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Sending...
                              </>
                            ) : (
                              <>
                                <i className="bi-send me-2"></i>
                                Send Message
                              </>
                            )}
                          </Button>
                        </motion.div>
                      </Form>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>

              {/* Contact Information */}
              <Col lg={4}>
                <motion.div variants={slideInRight}>
                  <div className="contact-info-section">
                    <h3 className="contact-info-title mb-4">Get in Touch</h3>
                    <p className="contact-info-description mb-4">
                      Have questions about NetSuite or need expert consultation? 
                      Our team is ready to help you succeed.
                    </p>
                    
                    <div className="contact-info-cards">
                      {contactInfo.map((info, index) => (
                        <motion.div
                          key={index}
                          variants={fadeInUp}
                          whileHover={{ y: -5, scale: 1.02 }}
                          className="contact-info-card mb-4"
                        >
                          <div className="contact-info-icon" style={{ backgroundColor: `${info.color}15`, color: info.color }}>
                            <i className={`bi ${info.icon}`}></i>
                          </div>
                          <div className="contact-info-content">
                            <h6 className="contact-info-card-title">{info.title}</h6>
                            {info.details.map((detail, detailIndex) => (
                              <p key={detailIndex} className="contact-info-detail">{detail}</p>
                            ))}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Trusted Clients Section */}
      <section className="trusted-clients-section py-5 bg-white">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            
            
            <Row className="g-4">
              {/* Partner Company Cards */}
              <Col lg={3} md={6} sm={6}>
                <motion.div 
                  variants={fadeInUp}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="partner-card"
                >
                  <Card className="partner-company-card border-0 shadow-sm h-100">
                    <Card.Body className="d-flex align-items-center justify-content-center p-4">
                      <div className="partner-logo-container">
                        <img 
                          src={company1} 
                          alt="Company 1 Logo" 
                          className="partner-logo"
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
              
              <Col lg={3} md={6} sm={6}>
                <motion.div 
                  variants={fadeInUp}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="partner-card"
                >
                  <Card className="partner-company-card border-0 shadow-sm h-100">
                    <Card.Body className="d-flex align-items-center justify-content-center p-4">
                      <div className="partner-logo-container">
                        <img 
                          src={company2} 
                          alt="Company 2 Logo" 
                          className="partner-logo"
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
              
              <Col lg={3} md={6} sm={6}>
                <motion.div 
                  variants={fadeInUp}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="partner-card"
                >
                  <Card className="partner-company-card border-0 shadow-sm h-100">
                    <Card.Body className="d-flex align-items-center justify-content-center p-4">
                      <div className="partner-logo-container">
                        <img 
                          src={company3}
                          alt="Company 3 Logo" 
                          className="partner-logo"
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
              
              <Col lg={3} md={6} sm={6}>
                <motion.div 
                  variants={fadeInUp}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="partner-card"
                >
                  <Card className="partner-company-card border-0 shadow-sm h-100">
                    <Card.Body className="d-flex align-items-center justify-content-center p-4">
                      <div className="partner-logo-container">
                        <img 
                          src={company4}
                          alt="Company 4 Logo" 
                          className="partner-logo"
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            </Row>
            <Row className="justify-content-center pt-5">
              <Col lg={8} className="text-center">
                <motion.div variants={fadeInUp} className="mb-5">
                  <h3 className="trusted-clients-title mb-3">
                    Over 50 clients trust us for NetSuite expertise
                  </h3>
                  <p className="trusted-clients-subtitle">
                    Partner with us to unlock your business potential
                  </p>
                </motion.div>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Additional Info Section */}
      <section className="additional-info-section py-5 bg-light">
        <Container>
          <motion.div
            ref={infoRef}
            initial="hidden"
            animate={infoInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <Row className="align-items-center">
              <Col lg={6}>
                <motion.div variants={slideInLeft}>
                  <h3 className="additional-info-title mb-4">Why Choose SpireNest?</h3>
                  <div className="why-choose-list">
                    <div className="why-choose-item">
                      <div className="why-choose-icon">
                        <i className="bi-award"></i>
                      </div>
                      <div>
                        <h6>Expert Team</h6>
                        <p>Certified NetSuite professionals with years of experience</p>
                      </div>
                    </div>
                    <div className="why-choose-item">
                      <div className="why-choose-icon">
                        <i className="bi-shield-check"></i>
                      </div>
                      <div>
                        <h6>Proven Track Record</h6>
                        <p>Successfully implemented 500+ NetSuite projects</p>
                      </div>
                    </div>
                    <div className="why-choose-item">
                      <div className="why-choose-icon">
                        <i className="bi-headset"></i>
                      </div>
                      <div>
                        <h6>24/7 Support</h6>
                        <p>Round-the-clock assistance whenever you need it</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </Col>
              <Col lg={6}>
                <motion.div variants={slideInRight} className="text-center">
                  <div className="contact-visual">
                    <motion.div 
                      className="contact-visual-icon"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    >
                      <i className="bi-headset"></i>
                    </motion.div>
                  </div>
                </motion.div>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>
    </div>
  );
};

export default ContactUs;